// OMS Operations Control hero — engraved banknote treatment.
// Visualises a control-room: medallion rosette, monitor wall trio, status ticker,
// live waveform, server rack, console desk, "always on" sun, right-edge turbine.
// Style strictly matches SoriginLogoBanner (engraved currency).
import '../../../../components/branding/SoriginLogoBanner.css';

export interface OmsControlHeroProps {
  className?: string;
}

export default function OmsControlHero({ className }: OmsControlHeroProps) {
  return (
    <svg
      viewBox="0 0 480 120"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-hidden="true"
      className={`sorigin-banner ${className ?? ''}`}
    >
      <defs>
        <linearGradient id="eb-oms-tint" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FBFEF9" />
          <stop offset="100%" stopColor="#F2F8EC" />
        </linearGradient>

        <linearGradient id="eb-oms-pole" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#006346" stopOpacity="0.72" />
          <stop offset="100%" stopColor="#076333" stopOpacity="0.55" />
        </linearGradient>

        <pattern
          id="eb-oms-microline"
          x="0"
          y="0"
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="6" stroke="#006346" strokeOpacity="0.05" strokeWidth="0.4" />
        </pattern>
      </defs>

      <style>{`
        .eb-oms-sun-spin {
          transform-origin: 0 0;
          animation: eb-oms-sun-rot 26s linear infinite;
          will-change: transform;
        }
        @keyframes eb-oms-sun-rot {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .eb-oms-pulse {
          animation: eb-oms-pulse-k 1.8s ease-in-out infinite;
          will-change: opacity;
        }
        @keyframes eb-oms-pulse-k {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        .eb-oms-waveform {
          animation: eb-oms-wave-k 4s ease-in-out infinite;
          will-change: transform;
        }
        @keyframes eb-oms-wave-k {
          0%, 100% { transform: translateX(0); }
          50%      { transform: translateX(-8px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .eb-oms-sun-spin,
          .eb-oms-pulse,
          .eb-oms-waveform { animation: none !important; }
        }
      `}</style>

      {/* Layer 1 — parchment tint */}
      <rect width="480" height="120" fill="url(#eb-oms-tint)" />

      {/* Layer 2 — diagonal micro-line hatch */}
      <rect width="480" height="120" fill="url(#eb-oms-microline)" />

      {/* Layer 3 — banknote double frame */}
      <rect x="5" y="5" width="470" height="110" rx="2" fill="none" stroke="#006346" strokeOpacity="0.22" strokeWidth="0.8" />
      <rect x="10" y="10" width="460" height="100" rx="1" fill="none" stroke="#52BF9D" strokeOpacity="0.32" strokeWidth="0.45" strokeDasharray="2 2.5" />

      {/* Layer 4 — corner flourishes + dots */}
      <g fill="none" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.9" strokeLinecap="round">
        <path d="M 5 18 Q 5 5 18 5" />
        <path d="M 462 5 Q 475 5 475 18" />
        <path d="M 475 102 Q 475 115 462 115" />
        <path d="M 18 115 Q 5 115 5 102" />
      </g>
      <g fill="#A6CE39" fillOpacity="0.85">
        <circle cx="11" cy="11" r="1.4" />
        <circle cx="469" cy="11" r="1.4" />
        <circle cx="469" cy="109" r="1.4" />
        <circle cx="11" cy="109" r="1.4" />
      </g>

      {/* Layer 5 — left rosette medallion */}
      <g transform="translate(60, 60)" fill="none" stroke="#006346" strokeWidth="0.5">
        <circle r="44" strokeOpacity="0.12" />
        <circle r="38" strokeOpacity="0.18" strokeDasharray="0.6 2" />
        <g strokeOpacity="0.10">
          <ellipse rx="34" ry="20" />
          <ellipse rx="34" ry="20" transform="rotate(30)" />
          <ellipse rx="34" ry="20" transform="rotate(60)" />
          <ellipse rx="34" ry="20" transform="rotate(90)" />
          <ellipse rx="34" ry="20" transform="rotate(120)" />
          <ellipse rx="34" ry="20" transform="rotate(150)" />
        </g>
        <circle r="26" strokeOpacity="0.16" />
        <circle r="14" strokeOpacity="0.10" />
      </g>

      {/* Layer 6 — engraved sun (upper-right, always-on) */}
      <g transform="translate(345, 28)">
        {/* rays rotate around (0,0) pivot */}
        <g className="eb-oms-sun-spin">
          <g fill="none" stroke="#006346" strokeOpacity="0.55" strokeWidth="0.55" strokeLinecap="round">
            {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
              <line key={a} x1="0" y1="-10" x2="0" y2="-14" transform={`rotate(${a})`} />
            ))}
          </g>
        </g>
        {/* concentric sun core */}
        <circle r="6" fill="none" stroke="#006346" strokeOpacity="0.6" strokeWidth="0.6" />
        <circle r="3.5" fill="none" stroke="#52BF9D" strokeOpacity="0.7" strokeWidth="0.5" />
        <circle r="1.4" fill="#A6CE39" fillOpacity="0.85" />
      </g>

      {/* Layer 7 — status ticker bar above the monitors */}
      <g>
        <rect
          x="130"
          y="22"
          width="200"
          height="6"
          rx="1.2"
          fill="none"
          stroke="#006346"
          strokeOpacity="0.5"
          strokeWidth="0.6"
        />
        <rect x="130" y="22" width="200" height="6" rx="1.2" fill="#52BF9D" fillOpacity="0.08" />
        {/* faint engraved tick marks inside ticker */}
        <g stroke="#006346" strokeOpacity="0.3" strokeWidth="0.4">
          <line x1="146" y1="24" x2="146" y2="26" />
          <line x1="170" y1="24" x2="170" y2="26" />
          <line x1="200" y1="24" x2="200" y2="26" />
          <line x1="230" y1="24" x2="230" y2="26" />
          <line x1="260" y1="24" x2="260" y2="26" />
          <line x1="290" y1="24" x2="290" y2="26" />
          <line x1="314" y1="24" x2="314" y2="26" />
        </g>
        {/* 4 status dots: 2 lime, 2 mint — one mint pulses */}
        <circle cx="156" cy="25" r="1.2" fill="#A6CE39" fillOpacity="0.85" />
        <circle cx="210" cy="25" r="1.2" fill="#52BF9D" fillOpacity="0.85" />
        <circle cx="260" cy="25" r="1.2" fill="#A6CE39" fillOpacity="0.85" />
        <circle cx="310" cy="25" r="1.2" fill="#52BF9D" fillOpacity="0.85" className="eb-oms-pulse" />
      </g>

      {/* Layer 8 — monitor wall (3 monitors) */}
      <g>
        {/* Monitor 1 — sparkline */}
        <g transform="translate(140, 32)">
          <rect x="0" y="0" width="52" height="38" rx="1.5" fill="#FBFEF9" stroke="#006346" strokeOpacity="0.85" strokeWidth="0.8" />
          <rect x="2" y="2" width="48" height="34" rx="1" fill="#F2F8EC" stroke="#52BF9D" strokeOpacity="0.7" strokeWidth="0.5" />
          {/* sparkline */}
          <polyline
            points="6,28 14,22 22,25 30,16 38,20 46,10"
            fill="none"
            stroke="#006346"
            strokeOpacity="0.6"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* end marker */}
          <circle cx="46" cy="10" r="1" fill="#A6CE39" fillOpacity="0.85" />
          {/* engraved baseline */}
          <line x1="4" y1="32" x2="48" y2="32" stroke="#006346" strokeOpacity="0.2" strokeWidth="0.3" strokeDasharray="0.6 1.4" />
        </g>

        {/* Monitor 2 — donut KPI */}
        <g transform="translate(202, 32)">
          <rect x="0" y="0" width="52" height="38" rx="1.5" fill="#FBFEF9" stroke="#006346" strokeOpacity="0.85" strokeWidth="0.8" />
          <rect x="2" y="2" width="48" height="34" rx="1" fill="#F2F8EC" stroke="#52BF9D" strokeOpacity="0.7" strokeWidth="0.5" />
          {/* donut — concentric rings */}
          <g transform="translate(26, 19)">
            <circle r="10" fill="none" stroke="#006346" strokeOpacity="0.35" strokeWidth="0.7" />
            <circle r="7" fill="none" stroke="#52BF9D" strokeOpacity="0.55" strokeWidth="0.6" />
            {/* highlighted quarter arc */}
            <path
              d="M 0 -10 A 10 10 0 0 1 10 0"
              fill="none"
              stroke="#A6CE39"
              strokeOpacity="0.95"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
            <circle r="1.4" fill="none" stroke="#006346" strokeOpacity="0.7" strokeWidth="0.4" />
            <circle r="0.6" fill="#006346" />
          </g>
        </g>

        {/* Monitor 3 — bars */}
        <g transform="translate(264, 32)">
          <rect x="0" y="0" width="52" height="38" rx="1.5" fill="#FBFEF9" stroke="#006346" strokeOpacity="0.85" strokeWidth="0.8" />
          <rect x="2" y="2" width="48" height="34" rx="1" fill="#F2F8EC" stroke="#52BF9D" strokeOpacity="0.7" strokeWidth="0.5" />
          {/* engraved baseline */}
          <line x1="6" y1="30" x2="46" y2="30" stroke="#006346" strokeOpacity="0.3" strokeWidth="0.4" />
          {/* 5 vertical bars */}
          <g fill="none" stroke="#006346" strokeOpacity="0.6" strokeWidth="0.8" strokeLinecap="round">
            <line x1="10" y1="30" x2="10" y2="22" />
            <line x1="18" y1="30" x2="18" y2="16" />
            <line x1="26" y1="30" x2="26" y2="20" />
            <line x1="34" y1="30" x2="34" y2="12" />
            <line x1="42" y1="30" x2="42" y2="18" />
          </g>
          {/* tallest bar tipped lime */}
          <circle cx="34" cy="12" r="0.9" fill="#A6CE39" fillOpacity="0.9" />
        </g>

        {/* shared desk shelf line beneath the monitor trio */}
        <line x1="135" y1="72" x2="321" y2="72" stroke="#076333" strokeOpacity="0.7" strokeWidth="0.5" />
        <line x1="135" y1="73.5" x2="321" y2="73.5" stroke="#076333" strokeOpacity="0.35" strokeWidth="0.35" strokeDasharray="1.2 1.8" />
      </g>

      {/* Layer 9 — engraved waveform (data flow) under monitor row */}
      <g className="eb-oms-waveform">
        <path
          d="M 128 88 Q 138 82 148 88 Q 158 94 168 88 Q 178 82 188 88 Q 198 94 208 88 Q 218 82 228 88 Q 238 94 248 88 Q 258 82 268 88 Q 278 94 288 88 Q 298 82 308 88 Q 318 94 328 88"
          fill="none"
          stroke="#006346"
          strokeOpacity="0.5"
          strokeWidth="0.5"
          strokeLinecap="round"
        />
        {/* faint echo for depth */}
        <path
          d="M 128 88 Q 138 82 148 88 Q 158 94 168 88 Q 178 82 188 88 Q 198 94 208 88 Q 218 82 228 88 Q 238 94 248 88 Q 258 82 268 88 Q 278 94 288 88 Q 298 82 308 88 Q 318 94 328 88"
          fill="none"
          stroke="#52BF9D"
          strokeOpacity="0.3"
          strokeWidth="0.35"
          strokeLinecap="round"
          transform="translate(0, 2)"
        />
      </g>

      {/* Layer 10 — server rack (right) */}
      <g>
        <rect
          x="370"
          y="35"
          width="30"
          height="60"
          rx="1.5"
          fill="#FBFEF9"
          stroke="#006346"
          strokeOpacity="0.75"
          strokeWidth="0.8"
        />
        {/* inner rack inset */}
        <rect
          x="372.5"
          y="37.5"
          width="25"
          height="55"
          rx="1"
          fill="none"
          stroke="#52BF9D"
          strokeOpacity="0.5"
          strokeWidth="0.4"
        />
        {/* 7 horizontal slot lines (1U units) */}
        <g stroke="#006346" strokeOpacity="0.55" strokeWidth="0.5" strokeLinecap="round">
          <line x1="374" y1="44" x2="396" y2="44" />
          <line x1="374" y1="51" x2="396" y2="51" />
          <line x1="374" y1="58" x2="396" y2="58" />
          <line x1="374" y1="65" x2="396" y2="65" />
          <line x1="374" y1="72" x2="396" y2="72" />
          <line x1="374" y1="79" x2="396" y2="79" />
          <line x1="374" y1="86" x2="396" y2="86" />
        </g>
        {/* faint dashed sub-rules between slots */}
        <g stroke="#076333" strokeOpacity="0.25" strokeWidth="0.3" strokeDasharray="1 1.4">
          <line x1="376" y1="47.5" x2="394" y2="47.5" />
          <line x1="376" y1="54.5" x2="394" y2="54.5" />
          <line x1="376" y1="61.5" x2="394" y2="61.5" />
          <line x1="376" y1="68.5" x2="394" y2="68.5" />
          <line x1="376" y1="75.5" x2="394" y2="75.5" />
          <line x1="376" y1="82.5" x2="394" y2="82.5" />
        </g>
        {/* 3 LED dots on the side */}
        <circle cx="378" cy="41" r="0.9" fill="#A6CE39" fillOpacity="0.85" />
        <circle cx="378" cy="46" r="0.9" fill="#A6CE39" fillOpacity="0.85" className="eb-oms-pulse" />
        <circle cx="378" cy="51" r="0.9" fill="#A6CE39" fillOpacity="0.85" />
      </g>

      {/* Layer 11 — foreground console / desk */}
      <g>
        <rect x="20" y="95" width="350" height="6" rx="1" fill="#076333" fillOpacity="0.4" />
        <line x1="20" y1="95" x2="370" y2="95" stroke="#006346" strokeOpacity="0.6" strokeWidth="0.5" />
        <line x1="20" y1="101" x2="370" y2="101" stroke="#076333" strokeOpacity="0.55" strokeWidth="0.4" />
        {/* engraved console buttons */}
        <g>
          <circle cx="140" cy="98" r="2" fill="none" stroke="#A6CE39" strokeOpacity="0.9" strokeWidth="0.6" />
          <circle cx="140" cy="98" r="0.7" fill="#A6CE39" fillOpacity="0.85" />
          <circle cx="180" cy="98" r="2" fill="none" stroke="#A6CE39" strokeOpacity="0.9" strokeWidth="0.6" />
          <circle cx="180" cy="98" r="0.7" fill="#52BF9D" fillOpacity="0.9" />
          <circle cx="220" cy="98" r="2" fill="none" stroke="#A6CE39" strokeOpacity="0.9" strokeWidth="0.6" />
          <circle cx="220" cy="98" r="0.7" fill="#A6CE39" fillOpacity="0.85" />
        </g>
      </g>

      {/* Layer 12 — ground hatching beneath console */}
      <g stroke="#006346" strokeOpacity="0.3" strokeWidth="0.5" strokeLinecap="round">
        <line x1="32" y1="106" x2="360" y2="106" strokeDasharray="1.2 2" />
        <line x1="48" y1="110" x2="346" y2="110" strokeDasharray="0.8 2.2" />
        <line x1="64" y1="114" x2="332" y2="114" strokeDasharray="0.6 2" />
      </g>

      {/* Layer 13 — right-edge wind turbine (scale ~0.55) */}
      <g transform="translate(455, 55) scale(0.55)">
        {/* contact shadow */}
        <ellipse cx="0" cy="48" rx="11" ry="1.3" fill="#006346" fillOpacity="0.22" />

        {/* tiny ground hatching */}
        <g stroke="#006346" strokeOpacity="0.35" strokeWidth="0.5" strokeLinecap="round">
          <line x1="-12" y1="51" x2="12" y2="51" />
          <line x1="-16" y1="54" x2="16" y2="54" strokeDasharray="1 2" />
          <line x1="-10" y1="57" x2="10" y2="57" strokeDasharray="0.6 2" />
        </g>

        {/* tapered pole */}
        <path d="M -1.6 47.5 L -0.45 -4 L 0.45 -4 L 1.6 47.5 Z" fill="url(#eb-oms-pole)" />

        {/* nacelle */}
        <path d="M -4 -2 L 0 -8 L 4 -2 Z" fill="#006346" fillOpacity="0.6" />
        <rect x="-2.5" y="-6.5" width="5" height="3.5" rx="0.6" fill="#006346" fillOpacity="0.55" />

        {/* hub + blades */}
        <g transform="translate(0, -5)">
          <g className="sb-turbine-blades">
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.7" />
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.7" transform="rotate(120)" />
            <path d="M 2.6 0 Q 12 -1.5 22 -0.6 Q 28 0 22 0.6 Q 12 1.5 2.6 0 Z" fill="#006346" fillOpacity="0.7" transform="rotate(240)" />
          </g>
          <circle r="2.8" fill="#A6CE39" />
          <circle r="2.8" fill="none" stroke="#006346" strokeOpacity="0.85" strokeWidth="0.5" />
          <circle r="1" fill="#006346" />
        </g>
      </g>

      {/* Layer 14 — tiny lime sparkles (one pulses) */}
      <g fill="#A6CE39" fillOpacity="0.65">
        <circle cx="118" cy="18" r="0.9" />
        <circle cx="356" cy="100" r="0.9" className="eb-oms-pulse" />
        <circle cx="432" cy="100" r="0.7" />
        <circle cx="100" cy="80" r="0.7" />
      </g>

      {/* Layer 15 — denomination-style serial corners (faint text-like marks) */}
      <g fill="#006346" fillOpacity="0.35">
        <rect x="22" y="20" width="14" height="1" rx="0.5" />
        <rect x="22" y="22.5" width="9" height="0.8" rx="0.4" />
        <rect x="444" y="98" width="14" height="1" rx="0.5" />
        <rect x="449" y="100.5" width="9" height="0.8" rx="0.4" />
      </g>
    </svg>
  );
}
